/**
 * InkingEditLayer — transformed page wrapper for the editable inking canvas.
 *
 * Owns the 2D pan/zoom CSS transform applied to the frame border + raster
 * canvas, plus the input plumbing that drives pen/eraser strokes. Only
 * active while `inkingMode.editSubMode === "edit"` and a raster buffer is
 * present.
 *
 * Input routing:
 *   - Pen:          PointerEvents always drive drawing (stylus pressure honoured).
 *   - Mouse:        PointerEvents drive drawing only when non-pen input is
 *                   enabled. Middle-button drag and alt+drag pan the page.
 *                   Wheel zooms around the cursor.
 *   - Touch:        Imperative TouchEvent listeners mirror the gesture stack
 *                   used by `ReferenceImageOverlay`. With non-pen input
 *                   disabled, one finger pans and two fingers pinch-zoom.
 *                   With it enabled, one finger draws and two fingers keep
 *                   the same pinch/pan navigation.
 *
 * Coordinate chain (screen → raster):
 *   (clientX, clientY) → stage.getBoundingClientRect() → normalised (0..1)
 *     → × (rasterWidth, rasterHeight)                  → raster pixel
 *
 * The transform is applied as `translate(pan) scale(zoom)` with
 * transformOrigin = `0 0`, so the page rect tracks the math above exactly.
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";
import { useStore } from "../../../app/store.ts";
import {
  MULTI_TAP_MAX_DURATION,
  MULTI_TAP_MAX_MOVE,
} from "../../interactionConstants.ts";
import { stampInkingSegment } from "./stampCore.ts";

interface FrameBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

interface Props {
  /** Frame box in wrapper-relative CSS pixels (the page extents). */
  frameBox: FrameBox;
  /** Target raster pixel size (matches renderWidth/renderHeight). */
  renderWidth: number;
  renderHeight: number;
}

interface StrokeInputSample {
  clientX: number;
  clientY: number;
  pointerType: string;
  pressure: number;
}

const DAB_SPACING_FRACTION = 0.15;
const MIN_ZOOM = 0.1;
const MAX_ZOOM = 16;

// ---------------------------------------------------------------------------
// Touch gesture helpers — mirrors ReferenceImageOverlay.tsx
// ---------------------------------------------------------------------------

function touchDistance(a: Touch, b: Touch): number {
  const dx = a.clientX - b.clientX;
  const dy = a.clientY - b.clientY;
  return Math.sqrt(dx * dx + dy * dy);
}

function touchAngle(a: Touch, b: Touch): number {
  return Math.atan2(b.clientY - a.clientY, b.clientX - a.clientX);
}

function touchCenter(a: Touch, b: Touch): [number, number] {
  return [(a.clientX + b.clientX) / 2, (a.clientY + b.clientY) / 2];
}

/** Apply a 2D rotation (in degrees) to a vector. */
function rotate2D(deg: number, x: number, y: number): [number, number] {
  const r = (deg * Math.PI) / 180;
  const c = Math.cos(r);
  const s = Math.sin(r);
  return [c * x - s * y, s * x + c * y];
}

/**
 * Drop stylus (Apple Pencil) touches so they never trigger pan/pinch — the
 * stylus must fall through to the PointerEvent drawing path. Matches the
 * `fingerTouches` filter in `MultiTouchHandler.tsx`.
 */
function fingerTouchList(list: TouchList): Touch[] {
  const out: Touch[] = [];
  for (let i = 0; i < list.length; i++) {
    // `touchType` is WebKit-only and reports "stylus" for Apple Pencil.
    if ((list[i] as unknown as { touchType?: string }).touchType !== "stylus") {
      out.push(list[i]);
    }
  }
  return out;
}

function hexToRgb01(hex: string): [number, number, number] {
  const m = hex.replace("#", "");
  const n =
    m.length === 3
      ? parseInt(
          m
            .split("")
            .map((c) => c + c)
            .join(""),
          16,
        )
      : parseInt(m, 16);
  return [((n >> 16) & 0xff) / 255, ((n >> 8) & 0xff) / 255, (n & 0xff) / 255];
}

export function InkingEditLayer({
  frameBox,
  renderWidth,
  renderHeight,
}: Props) {
  const inkingMode = useStore((s) => s.inkingMode);
  const setInkingPan = useStore((s) => s.setInkingPan);
  const setInkingZoom = useStore((s) => s.setInkingZoom);
  const setInkingRotation = useStore((s) => s.setInkingRotation);
  const endInkingStroke = useStore((s) => s.endInkingStroke);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const strokeActiveRef = useRef(false);
  const lastDabRef = useRef<{ x: number; y: number; pressure: number } | null>(
    null,
  );
  const pendingMoveRef = useRef<StrokeInputSample | null>(null);
  const rafRef = useRef<number | null>(null);
  // Hold a live reference to the raster we're stamping into. Set at stroke
  // start; mutated in place per dab; cleared at stroke end. We only bump
  // rasterSeq in the store on stroke-end so subscribers don't redraw per-dab.
  const liveRasterRef = useRef<Uint8ClampedArray | null>(null);

  const editing = inkingMode?.editSubMode === "edit";
  const rasterBuffer = inkingMode?.rasterBuffer ?? null;
  const pan = inkingMode?.pan ?? [0, 0];
  const zoom = inkingMode?.zoom ?? 1;
  const rotation = inkingMode?.rotation ?? 0;
  const allowNonPenDrawingInput = useStore((s) => s.allowNonPenDrawingInput);

  // Sync the visible edit canvas with the raster buffer whenever it changes
  // (including undo/redo, re-bake, and initial bake). Draws at 1:1 pixel
  // resolution; CSS scaling handles display size.
  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !rasterBuffer) return;
    if (canvas.width !== renderWidth) canvas.width = renderWidth;
    if (canvas.height !== renderHeight) canvas.height = renderHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const imgData = ctx.createImageData(renderWidth, renderHeight);
    imgData.data.set(rasterBuffer);
    ctx.putImageData(imgData, 0, 0);
  }, [rasterBuffer, renderWidth, renderHeight]);

  /** Wrapper-relative screen origin of the frame (pre-transform top-left). */
  const getFrameOrigin = useCallback((): { x: number; y: number } | null => {
    const el = wrapperRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    return { x: rect.left + frameBox.left, y: rect.top + frameBox.top };
  }, [frameBox.left, frameBox.top]);

  /**
   * Transform a client point to raster pixel space, taking the live
   * translate/rotate/scale into account. Inverting the chain in order:
   *   1. subtract frame origin + pan  → vector from the rotated frame's origin
   *   2. rotate by -rotation          → vector in the frame's local (unrotated) frame
   *   3. divide by zoom               → CSS pixels on the page
   *   4. × (renderWidth / frameWidth) → raster pixels
   */
  const clientToRaster = useCallback(
    (clientX: number, clientY: number): { x: number; y: number } | null => {
      const origin = getFrameOrigin();
      if (!origin || frameBox.width <= 0 || frameBox.height <= 0) return null;
      const dx = clientX - origin.x - pan[0];
      const dy = clientY - origin.y - pan[1];
      const [lx, ly] = rotate2D(-rotation, dx, dy);
      const cssX = lx / zoom;
      const cssY = ly / zoom;
      return {
        x: cssX * (renderWidth / frameBox.width),
        y: cssY * (renderHeight / frameBox.height),
      };
    },
    [
      getFrameOrigin,
      frameBox.width,
      frameBox.height,
      pan,
      zoom,
      rotation,
      renderWidth,
      renderHeight,
    ],
  );

  /**
   * Brush size is stored as a raster-pixel radius. Returning the raw value
   * means a size=3 stroke is always 3 raster pixels wide regardless of the
   * frame's display size, so the export looks like the preview. The on-screen
   * visual thickness changes with zoom, as expected for a pen.
   */
  const radiusInRasterPx = useCallback((size: number): number => size, []);

  /**
   * Apply a zoom delta around a fixed screen point, keeping that point
   * stationary in page-local coordinates. `panBasis`/`zoomBasis`/`rotationBasis`
   * let callers apply the change relative to a gesture-start snapshot (used by
   * pinch so it doesn't drift). Rotation is preserved here; pinch applies
   * rotation separately via `applyPinchTransform`.
   */
  const zoomAroundPoint = useCallback(
    (
      cursorX: number,
      cursorY: number,
      nextZoom: number,
      panBasis?: [number, number],
      zoomBasis?: number,
      rotationBasis?: number,
    ) => {
      const origin = getFrameOrigin();
      if (!origin) return;
      const im = useStore.getState().inkingMode;
      if (!im) return;
      const prevPan = panBasis ?? (im.pan as [number, number]);
      const prevZoom = zoomBasis ?? im.zoom;
      const prevRot = rotationBasis ?? im.rotation;
      const clampedNext = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, nextZoom));

      // Page-local CSS px of the cursor under the basis transform:
      //   local = R(-rot) · (cursor - frameOrigin - pan) / zoom
      const dx = cursorX - origin.x - prevPan[0];
      const dy = cursorY - origin.y - prevPan[1];
      const [lxRotated, lyRotated] = rotate2D(-prevRot, dx, dy);
      const localX = lxRotated / prevZoom;
      const localY = lyRotated / prevZoom;

      // Solve for the new pan that keeps `local` under `cursor`:
      //   cursor = frameOrigin + panNew + R(rot) · zoomNew · local
      const [rx, ry] = rotate2D(
        prevRot,
        localX * clampedNext,
        localY * clampedNext,
      );
      setInkingZoom(clampedNext);
      setInkingPan([cursorX - origin.x - rx, cursorY - origin.y - ry]);
    },
    [getFrameOrigin, setInkingPan, setInkingZoom],
  );

  /**
   * Apply a full pinch transform — rotation + scale about the gesture-start
   * centroid, plus centroid translation to follow the fingers.
   *
   * Solved directly from the constraint that the frame-local point under the
   * initial centroid must map to the current centroid:
   *   centerCurrent = frameOrigin + panNew + R(rotNew) · zoomNew · localStart
   */
  const applyPinchTransform = useCallback(
    (
      centerStart: [number, number],
      centerCurrent: [number, number],
      deltaAngleDeg: number,
      ratio: number,
      panStart: [number, number],
      zoomStart: number,
      rotationStart: number,
    ) => {
      const origin = getFrameOrigin();
      if (!origin) return;

      // Frame-local (CSS) point under the initial centroid.
      const dx = centerStart[0] - origin.x - panStart[0];
      const dy = centerStart[1] - origin.y - panStart[1];
      const [lxRotated, lyRotated] = rotate2D(-rotationStart, dx, dy);
      const localX = lxRotated / zoomStart;
      const localY = lyRotated / zoomStart;

      const nextRot = rotationStart + deltaAngleDeg;
      const nextZoom = Math.max(
        MIN_ZOOM,
        Math.min(MAX_ZOOM, zoomStart * ratio),
      );
      const [rx, ry] = rotate2D(nextRot, localX * nextZoom, localY * nextZoom);
      const panX = centerCurrent[0] - origin.x - rx;
      const panY = centerCurrent[1] - origin.y - ry;

      setInkingZoom(nextZoom);
      setInkingRotation(nextRot);
      setInkingPan([panX, panY]);
    },
    [getFrameOrigin, setInkingPan, setInkingRotation, setInkingZoom],
  );

  const processMove = useCallback(
    (e: StrokeInputSample) => {
      if (!strokeActiveRef.current) return;
      const raster = liveRasterRef.current;
      const im = useStore.getState().inkingMode;
      if (!raster || !im) return;

      const pt = clientToRaster(e.clientX, e.clientY);
      if (!pt) return;

      // Inking is a line-art tool: a mouse with no native pressure should
      // still land opaque ink. Sketch mode uses 0.5 as a soft fallback because
      // it's designed for layered glazing; inking prefers 1.0 so the default
      // mouse stroke reads as a solid line. The pen branch preserves native
      // pressure sensitivity.
      const pressure =
        e.pointerType === "pen" ? Math.max(0.05, e.pressure) : 1.0;

      // Pressure mode: "size" scales the dab radius with pressure; "opacity"
      // scales the flow only. This matches SketchModeState.pressureMode.
      const baseRadius = radiusInRasterPx(im.size);
      const effectiveRadius =
        im.pressureMode === "size"
          ? Math.max(0.5, baseRadius * (0.15 + 0.85 * pressure))
          : baseRadius;
      const effectivePressure = im.pressureMode === "size" ? 1 : pressure;

      const [cr, cg, cb] = hexToRgb01(im.color);
      const spacingPx = Math.max(1, effectiveRadius * DAB_SPACING_FRACTION * 2);

      const result = stampInkingSegment(
        raster,
        im.rasterWidth,
        im.rasterHeight,
        lastDabRef.current,
        { x: pt.x, y: pt.y, pressure: effectivePressure },
        {
          radius: effectiveRadius,
          hardness: im.hardness,
          cr,
          cg,
          cb,
          opacity: im.opacity,
          spacing: DAB_SPACING_FRACTION,
          erase: im.tool === "eraser",
        },
        spacingPx,
      );
      lastDabRef.current = result.anchor;

      if (result.committed > 0) {
        // Commit the mutated raster to the canvas. `createImageData` is used
        // (rather than `new ImageData(raster, ...)`) because it returns a
        // typed array with a concrete ArrayBuffer backing — TypeScript's
        // default `Uint8ClampedArray<ArrayBufferLike>` can't satisfy the
        // ImageData constructor's stricter signature. The copy is a single
        // memcpy and stays comfortably inside a frame budget.
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext("2d");
        if (ctx && canvas) {
          const imgData = ctx.createImageData(im.rasterWidth, im.rasterHeight);
          imgData.data.set(raster);
          ctx.putImageData(imgData, 0, 0);
        }
      }
    },
    [clientToRaster, radiusInRasterPx],
  );

  // --- Pen / mouse drawing via PointerEvents (touch filtered out and
  //     handled imperatively below). ---

  const beginStroke = useCallback(
    (ev: StrokeInputSample): boolean => {
      const store = useStore.getState();
      if (!store.inkingMode || store.inkingMode.editSubMode !== "edit")
        return false;
      if (!store.inkingMode.rasterBuffer) return false;

      store.pushUndo();
      liveRasterRef.current = store.inkingMode.rasterBuffer;
      strokeActiveRef.current = true;
      lastDabRef.current = null;
      processMove(ev);
      return true;
    },
    [processMove],
  );

  const endStroke = useCallback(() => {
    if (!strokeActiveRef.current) return;
    strokeActiveRef.current = false;
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    const pending = pendingMoveRef.current;
    pendingMoveRef.current = null;
    if (pending) processMove(pending);

    lastDabRef.current = null;
    liveRasterRef.current = null;
    endInkingStroke();
  }, [endInkingStroke, processMove]);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      // Touch is routed through the TouchEvent path below.
      if (e.pointerType === "touch") return;
      if (e.pointerType === "mouse" && !allowNonPenDrawingInput) return;
      if (e.button !== 0) return;
      if (e.altKey) return;
      const stage = stageRef.current;
      if (!stage) return;
      if (!beginStroke(e.nativeEvent)) return;
      stage.setPointerCapture(e.pointerId);
      e.preventDefault();
    },
    [allowNonPenDrawingInput, beginStroke],
  );

  // Coalesce pointermove events to one per frame to keep stroke cost bounded
  // even under spammy input (e.g. stylus with high report rate).
  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.pointerType === "touch") return;
      if (!strokeActiveRef.current) return;
      pendingMoveRef.current = e.nativeEvent;
      if (rafRef.current == null) {
        rafRef.current = requestAnimationFrame(() => {
          rafRef.current = null;
          const pending = pendingMoveRef.current;
          pendingMoveRef.current = null;
          if (pending) processMove(pending);
        });
      }
    },
    [processMove],
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.pointerType === "touch") return;
      if (!strokeActiveRef.current) return;
      endStroke();
      try {
        (e.currentTarget as HTMLDivElement).releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
    },
    [endStroke],
  );

  // --- Mouse middle-button / alt+left-button pan ---

  const panStateRef = useRef<{
    startX: number;
    startY: number;
    startPan: [number, number];
    pointerId: number;
  } | null>(null);

  const handlePanDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const isMiddle = e.button === 1;
    const isAltLeft = e.button === 0 && e.altKey;
    const isRight = e.button === 2;
    if (!isMiddle && !isAltLeft && !isRight) return;
    const im = useStore.getState().inkingMode;
    if (!im) return;
    panStateRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startPan: [im.pan[0], im.pan[1]],
      pointerId: e.pointerId,
    };
    (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handlePanMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const ps = panStateRef.current;
      if (!ps || ps.pointerId !== e.pointerId) return;
      const dx = e.clientX - ps.startX;
      const dy = e.clientY - ps.startY;
      setInkingPan([ps.startPan[0] + dx, ps.startPan[1] + dy]);
    },
    [setInkingPan],
  );

  const handlePanUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const ps = panStateRef.current;
    if (!ps || ps.pointerId !== e.pointerId) return;
    panStateRef.current = null;
    try {
      (e.currentTarget as HTMLDivElement).releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  }, []);

  // --- Wheel zoom (mouse + trackpad) ---

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el || !editing) return;

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const im = useStore.getState().inkingMode;
      if (!im) return;
      const factor = e.deltaY < 0 ? 1.1 : 1 / 1.1;
      zoomAroundPoint(e.clientX, e.clientY, im.zoom * factor);
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [editing, zoomAroundPoint]);

  // --- Touch gestures: 1-finger pan/draw, 2-finger pinch-zoom + pan ---
  //
  // Mirrors the ReferenceImageOverlay pattern: imperative listeners with
  // `passive: false` so preventDefault suppresses the synthesised PointerEvents
  // and browser gestures (page scroll, pinch-zoom of the entire viewport).

  // Gesture state lives in refs (not effect-local consts) so it survives
  // re-renders. applyPinchTransform updates pan/zoom/rotation in the store
  // on every touchmove; those updates flow back through `clientToRaster` →
  // `processMove` → `beginStroke`/`endStroke`, which would re-run the
  // listener effect mid-gesture and reset `pinchStart.active` to false —
  // every two-finger pinch visibly dropped on the first move frame.
  const pinchStartRef = useRef({
    active: false,
    dist: 0,
    angle: 0,
    center: [0, 0] as [number, number],
    pan: [0, 0] as [number, number],
    zoom: 1,
    rotation: 0,
  });
  const panStartRef = useRef({
    active: false,
    x: 0,
    y: 0,
    pan: [0, 0] as [number, number],
  });
  // Multi-finger tap tracking — mirrors MultiTouchHandler.tsx so 2/3-finger
  // taps still trigger undo/redo inside the edit layer (the MultiTouchHandler
  // attached to the 3D canvas never sees our intercepted touches). A tap is
  // a short gesture (<MULTI_TAP_MAX_DURATION) where no finger ever moved
  // beyond MULTI_TAP_MAX_MOVE from its landing position.
  const tapStateRef = useRef({
    startTime: 0,
    maxFingers: 0,
    moved: false,
    startPositions: new Map<number, { x: number; y: number }>(),
  });
  // Latest callback snapshot — refreshed every render so touch handlers
  // always invoke current closures without pulling unstable callbacks into
  // the effect's deps and forcing a re-subscribe mid-gesture.
  const touchDepsRef = useRef({
    applyPinchTransform,
    beginStroke,
    endStroke,
    processMove,
    setInkingPan,
  });
  touchDepsRef.current = {
    applyPinchTransform,
    beginStroke,
    endStroke,
    processMove,
    setInkingPan,
  };

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el || !editing) return;

    const pinchStart = pinchStartRef.current;
    const panStart = panStartRef.current;
    const tap = tapStateRef.current;

    // Clear any stale state from a prior edit session so a fresh entry
    // doesn't inherit `active: true` from a gesture that was cancelled.
    pinchStart.active = false;
    panStart.active = false;
    tap.startTime = 0;
    tap.maxFingers = 0;
    tap.moved = false;
    tap.startPositions.clear();

    const endAll = () => {
      pinchStart.active = false;
      panStart.active = false;
      tap.startPositions.clear();
      tap.maxFingers = 0;
      tap.moved = false;
    };

    const onTouchStart = (e: TouchEvent) => {
      const im = useStore.getState().inkingMode;
      if (!im) return;
      // Stylus never navigates — let the Pencil pass through to the
      // PointerEvent drawing path. Only finger touches reach gesture logic.
      const fingers = fingerTouchList(e.touches);

      // Multi-tap bookkeeping: remember each new finger's landing position
      // and the peak simultaneous finger count so the touchend handler can
      // decide if the gesture was a tap.
      if (tap.startPositions.size === 0) tap.startTime = performance.now();
      for (const t of fingerTouchList(e.changedTouches)) {
        if (!tap.startPositions.has(t.identifier)) {
          tap.startPositions.set(t.identifier, { x: t.clientX, y: t.clientY });
        }
      }
      if (fingers.length > tap.maxFingers) tap.maxFingers = fingers.length;

      if (fingers.length === 1) {
        e.preventDefault();
        pinchStart.active = false;
        if (useStore.getState().allowNonPenDrawingInput) {
          panStart.active = false;
          tap.moved = true;
          const finger = fingers[0];
          if (!strokeActiveRef.current) {
            touchDepsRef.current.beginStroke({
              clientX: finger.clientX,
              clientY: finger.clientY,
              pointerType: "touch",
              pressure: 1,
            });
          }
        } else {
          panStart.active = true;
          panStart.x = fingers[0].clientX;
          panStart.y = fingers[0].clientY;
          panStart.pan = [im.pan[0], im.pan[1]];
        }
      } else if (fingers.length >= 2) {
        e.preventDefault();
        if (strokeActiveRef.current) touchDepsRef.current.endStroke();
        const a = fingers[0];
        const b = fingers[1];
        pinchStart.active = true;
        pinchStart.dist = touchDistance(a, b);
        pinchStart.angle = touchAngle(a, b);
        pinchStart.center = touchCenter(a, b);
        pinchStart.pan = [im.pan[0], im.pan[1]];
        pinchStart.zoom = im.zoom;
        pinchStart.rotation = im.rotation;
        panStart.active = false;
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      const fingers = fingerTouchList(e.touches);

      // Disqualify the gesture from being a tap as soon as any finger
      // travels beyond the threshold. Checking every moving touch (not just
      // the two or three we're tracking for pinch/pan) keeps the detection
      // consistent regardless of which branch below engages.
      if (!tap.moved) {
        for (const t of fingerTouchList(e.changedTouches)) {
          const start = tap.startPositions.get(t.identifier);
          if (!start) continue;
          const dx = t.clientX - start.x;
          const dy = t.clientY - start.y;
          if (dx * dx + dy * dy > MULTI_TAP_MAX_MOVE * MULTI_TAP_MAX_MOVE) {
            tap.moved = true;
            break;
          }
        }
      }

      if (fingers.length >= 2 && pinchStart.active) {
        e.preventDefault();
        const a = fingers[0];
        const b = fingers[1];
        if (pinchStart.dist < 1) return;

        // Scale ratio and rotation delta measured from gesture-start so the
        // transform reproduces directly from the initial snapshot. A single
        // solve (in applyPinchTransform) handles translate + rotate + scale
        // atomically so the gesture-start centroid stays pinned under the
        // fingers throughout the pinch.
        const ratio = touchDistance(a, b) / pinchStart.dist;
        const deltaAngleDeg =
          ((touchAngle(a, b) - pinchStart.angle) * 180) / Math.PI;
        touchDepsRef.current.applyPinchTransform(
          pinchStart.center,
          touchCenter(a, b),
          deltaAngleDeg,
          ratio,
          pinchStart.pan,
          pinchStart.zoom,
          pinchStart.rotation,
        );
      } else if (fingers.length === 1) {
        e.preventDefault();
        const t = fingers[0];
        if (strokeActiveRef.current) {
          touchDepsRef.current.processMove({
            clientX: t.clientX,
            clientY: t.clientY,
            pointerType: "touch",
            pressure: 1,
          });
        } else if (panStart.active) {
          const dx = t.clientX - panStart.x;
          const dy = t.clientY - panStart.y;
          touchDepsRef.current.setInkingPan([
            panStart.pan[0] + dx,
            panStart.pan[1] + dy,
          ]);
        }
      }
    };

    const maybeFireMultiTap = () => {
      if (tap.maxFingers < 2) return;
      if (tap.moved) return;
      const elapsed = performance.now() - tap.startTime;
      if (elapsed > MULTI_TAP_MAX_DURATION) return;
      const state = useStore.getState();
      if (tap.maxFingers === 2) state.undo();
      else if (tap.maxFingers === 3) state.redo();
      else if (tap.maxFingers >= 4) state.toggleChromeHidden();
    };

    const onTouchEnd = (e: TouchEvent) => {
      const fingers = fingerTouchList(e.touches);
      if (fingers.length === 0) {
        if (strokeActiveRef.current) touchDepsRef.current.endStroke();
        maybeFireMultiTap();
        endAll();
        return;
      }
      const im = useStore.getState().inkingMode;
      if (!im) return;
      // Going 2→1: drop pinch, re-anchor single-finger pan at the remaining
      // touch so the page doesn't jump. When touch drawing is enabled we
      // require a fresh touchstart before starting another stroke.
      if (fingers.length === 1) {
        pinchStart.active = false;
        if (useStore.getState().allowNonPenDrawingInput) {
          panStart.active = false;
        } else {
          panStart.active = true;
          panStart.x = fingers[0].clientX;
          panStart.y = fingers[0].clientY;
          panStart.pan = [im.pan[0], im.pan[1]];
        }
      }
    };

    el.addEventListener("touchstart", onTouchStart, { passive: false });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd);
    el.addEventListener("touchcancel", onTouchEnd);
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [editing]);

  const stageTransform = useMemo(
    () =>
      `translate(${pan[0]}px, ${pan[1]}px) rotate(${rotation}deg) scale(${zoom})`,
    [pan, zoom, rotation],
  );

  if (!editing || !frameBox) return null;

  return (
    <div
      ref={wrapperRef}
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "auto",
        overflow: "hidden",
        touchAction: "none",
      }}
      onPointerDown={(e) => {
        // Pan gestures take priority over stroke starts.
        // Right-button matches the 3D viewport's OrbitControls pan binding.
        if (e.button === 1 || e.button === 2 || (e.button === 0 && e.altKey)) {
          handlePanDown(e);
          return;
        }
        handlePointerDown(e);
      }}
      onPointerMove={(e) => {
        if (panStateRef.current) {
          handlePanMove(e);
          return;
        }
        handlePointerMove(e);
      }}
      onPointerUp={(e) => {
        if (panStateRef.current) {
          handlePanUp(e);
          return;
        }
        handlePointerUp(e);
      }}
      onPointerCancel={(e) => {
        if (panStateRef.current) handlePanUp(e);
        else handlePointerUp(e);
      }}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div
        ref={stageRef}
        style={{
          position: "absolute",
          left: frameBox.left,
          top: frameBox.top,
          width: frameBox.width,
          height: frameBox.height,
          transformOrigin: "0 0",
          transform: stageTransform,
          // White page background so erased pixels read as paper rather than
          // the (faded) 3D scene. When showReference is on the user can still
          // see the reference through the edit canvas because the backdrop
          // layer is painted *outside* this stage element.
          background: "#ffffff",
          boxShadow:
            "0 0 0 1px rgba(0, 0, 0, 0.25), 0 6px 20px rgba(0, 0, 0, 0.18)",
          willChange: "transform",
        }}
      >
        <canvas
          ref={canvasRef}
          width={renderWidth}
          height={renderHeight}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            imageRendering: "auto",
            pointerEvents: "none",
          }}
        />
      </div>
    </div>
  );
}
